// src/components/LocationPicker.js
import React, { useState } from 'react';

export default function LocationPicker({ 
  onLocationCapture, 
  label = "📍 Capture GPS Location",
  autoCapture = false 
}) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        
        setLocation(locationData);
        setLoading(false);
        
        if (onLocationCapture) {
          onLocationCapture(locationData);
        }
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Location permission denied. Please enable GPS access.');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location unavailable. Please try again.');
            break;
          case err.TIMEOUT:
            setError('Location request timed out. Please try again.');
            break;
          default:
            setError('Unable to get location. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const clearLocation = () => {
    setLocation(null);
    setError(null);
    if (onLocationCapture) {
      onLocationCapture(null);
    }
  };

  return (
    <div style={{
      background: '#0f2419',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #2d5a45'
    }}>
      <label style={{ 
        display: 'block', 
        color: '#4ade80', 
        marginBottom: '12px',
        fontWeight: 'bold'
      }}>
        {label}
      </label>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '10px',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {location ? (
        <div>
          <div style={{
            background: '#1a3327',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}>
            <div style={{ color: '#4ade80', marginBottom: '8px', fontWeight: 'bold' }}>
              ✓ Location Captured
            </div>
            <div style={{ color: '#888', fontSize: '13px' }}>
              <div>Latitude: {location.latitude.toFixed(6)}</div>
              <div>Longitude: {location.longitude.toFixed(6)}</div>
              <div>Accuracy: ±{Math.round(location.accuracy)}m</div>
            </div>
            <a 
              href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4ade80', fontSize: '12px' }}
            >
              View on Google Maps →
            </a>
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={captureLocation}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              📍 Recapture
            </button>
            <button
              type="button"
              onClick={clearLocation}
              style={{
                padding: '8px 16px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              ✕ Clear
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={captureLocation}
          disabled={loading}
          style={{
            padding: '12px 24px',
            background: loading ? '#666' : '#4ade80',
            color: loading ? 'white' : '#0f2419',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'wait' : 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            width: '100%'
          }}
        >
          {loading ? '📍 Getting location...' : '📍 Capture GPS Location'}
        </button>
      )}

      <p style={{ 
        color: '#666', 
        fontSize: '11px', 
        margin: '12px 0 0 0' 
      }}>
        GPS coordinates help verify your farm location for carbon credit validation.
      </p>
    </div>
  );
}